import React from 'react';
import { Box, Typography, Button, Avatar } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate } from 'react-router-dom';
import ProfileServiceCard from '../publicProfile/ProfileServiceCard';
import ProfilePageCard from '../publicProfile/ProfilePageCard';

// Muestra ~5 tarjetas (≈216px c/u) y activa scroll cuando hay más
const scrollSx = (count, itemHeight = 216) => count > 5 ? {
  maxHeight: itemHeight * 5.4,
  overflowY: 'auto',
  pr: 1,
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'grey.400', borderRadius: '10px' }
} : {};

// `total`: cuántos favoritos tiene la persona; con un filtro puestos se ven menos (`favorites`)
const FavoritesList = ({ favorites, total }) => {
  const navigate = useNavigate();

  if (!favorites || favorites.length === 0) {
    return (
      <Box 
        sx={{ 
          textAlign: 'center', 
          py: 12,
          px: 4,
          bgcolor: 'white',
          borderRadius: '24px',
          border: 1, borderColor: 'sutil',
          boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '600px',
          gap: 3
        }}
      >
        <Avatar 
          sx={{ 
            bgcolor: 'warning.main', 
            width: 100, 
            height: 100,
            mb: 1
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 60, color: 'white' }} />
        </Avatar>
        
        <Box sx={{ maxWidth: '500px' }}>
          <Typography variant="h4" fontWeight="bold" color="primary.main" sx={{ mb: 2 }}>
            Aún no tienes servicios guardados
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
            Cuando marques un servicio como favorito, aparecerá aquí para que puedas volver a él fácilmente cuando lo necesites.
          </Typography>
        </Box>

        <Button 
          variant="contained" 
          onClick={() => navigate('/')}
          sx={{ 
            bgcolor: 'primary.main', 
            textTransform: 'none', 
            px: 4, 
            py: 1.5,
            fontWeight: 'bold',
            borderRadius: '8px',
            '&:hover': { bgcolor: 'primary.dark' }
          }}
        >
          Explorar servicios
        </Button>
      </Box>
    );
  }

  // La pantalla ya es la lista completa: junto al título, cuántos son (y con un filtro, cuántos se ven de cuántos)
  const conteo = total && total !== favorites.length ? `${favorites.length} de ${total}` : `${favorites.length}`;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" color="primary">
          Tus Favoritos <Box component="span" sx={{ color: 'text.secondary', fontWeight: 'normal' }}>({conteo})</Box>
        </Typography>
        <Typography variant="body2" color="text.secondary">Servicios y páginas que has guardado</Typography>
      </Box>

      <Box>
        {/* Sección de Servicios Individuales */}
        {favorites.some(item => !item.isPage) && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 4, height: 16, bgcolor: 'primary.main', borderRadius: 1 }} />
              Servicios Individuales
            </Typography>
            <Box sx={scrollSx(favorites.filter(item => !item.isPage).length)}>
              {favorites.filter(item => !item.isPage).map(item => (
                <ProfileServiceCard
                  key={item._id}
                  service={item}
                  user={{ _id: item.userId || '', name: item.authorName || 'Proveedor', avatar: item.authorAvatar }}
                  imparte={item.imparte}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Sección de Páginas de Servicios */}
        {favorites.some(item => item.isPage) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 4, height: 16, bgcolor: 'warning.main', borderRadius: 1 }} />
              Páginas de Grupos y Negocios
            </Typography>
            <Box sx={scrollSx(favorites.filter(item => item.isPage).length)}>
              {favorites.filter(item => item.isPage).map(item => (
                <ProfilePageCard
                  key={item._id}
                  page={item}
                  user={{ _id: item.userId || '', name: item.authorName || 'Proveedor', avatar: item.authorAvatar }}
                  isGroup={item.tipo ? item.tipo === 'grupo' : item.membersCount > 1}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box >
  );
};

export default FavoritesList;
